import { useCallback, useEffect, useRef } from 'react';

import { useUniqueId } from 'utils/hooks';
import { useViewportSizeEffect } from 'utils/screen';

import { TabSwitcherTab } from './Tab';
import {
  TabSwitcherContainer,
  TabSwitcherIndicator,
  TabSwitcherTabsContainer,
} from './TabSwitcher.styled';
import { TabSwitcherTabData } from './TabSwitcherTabData';

type Props<T extends string> = {
  tabs: TabSwitcherTabData<T>[];
  value: T | undefined;
  onSelectTab: (name: T) => void;
};

export function TabSwitcher<T extends string>({
  tabs,
  value,
  onSelectTab,
}: Props<T>) {
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);

  const selectedTabIndex = value
    ? tabs.findIndex((tab) => tab.value === value)
    : -1;

  const moveIndicator = useCallback(() => {
    const tabsContainer = tabsContainerRef.current;
    const indicator = indicatorRef.current;

    if (!tabsContainer || !indicator || selectedTabIndex === -1) return;

    // each tab has 2 elements, an input and a label
    const tabNode = tabsContainer.childNodes[selectedTabIndex * 2 + 1];
    if (!tabNode) return;

    const { offsetLeft, offsetWidth } = tabNode as HTMLDivElement;
    indicator.style.width = `${offsetWidth}px`;
    indicator.style.left = `${offsetLeft}px`;
  }, [selectedTabIndex]);

  useEffect(() => {
    moveIndicator();
  }, [moveIndicator]);

  useViewportSizeEffect(() => {});

  const tabInputName = useUniqueId({ prefix: 'tab-switcher-input' });

  return (
    <TabSwitcherContainer>
      <TabSwitcherTabsContainer ref={tabsContainerRef}>
        {tabs.map((tab) => (
          <TabSwitcherTab
            key={tab.value}
            name={tabInputName}
            isSelected={tab.value === value}
            onChange={() => onSelectTab(tab.value)}
            qaId={tab.qaId}
          >
            {tab.label}
          </TabSwitcherTab>
        ))}
      </TabSwitcherTabsContainer>

      <TabSwitcherIndicator
        ref={indicatorRef}
        style={{ opacity: selectedTabIndex === -1 ? 0 : undefined }}
      />
    </TabSwitcherContainer>
  );
}
